/**
 * Public Intake Form API Client
 *
 * Plain fetch() calls - no authentication required.
 * Used by the public-facing intake form page.
 */

import { IntakeFieldResponse, DeviceSystemInfo, IntakeFormConfig } from './types';

const API_BASE = '/api';

interface IntakeSessionResponse {
  ticketNumber: string;
  companyName: string;
  endUserName: string;
  formConfig: IntakeFormConfig;
  companyDevices: Array<{ deviceId: number; hostname: string }>;
}

interface DeviceInfoResponse {
  deviceId: number;
  hostname: string;
  screenConnectGuid?: string;
  systemInfo?: DeviceSystemInfo;
}

export async function getIntakeSession(token: string): Promise<{
  success: boolean;
  data?: IntakeSessionResponse;
  error?: string;
  sessionId?: string;
  submittedAt?: string;
}> {
  const response = await fetch(`${API_BASE}/intake/sessions/${token}`);
  return await response.json() as {
    success: boolean;
    data?: IntakeSessionResponse;
    error?: string;
    sessionId?: string;
    submittedAt?: string;
  };
}

export async function submitIntakeForm(token: string, data: {
  fields: IntakeFieldResponse[];
  detectedDeviceId?: number;
  detectedHostname?: string;
  screenConnectGuid?: string;
  systemInfo?: DeviceSystemInfo;
  deviceDetectionMethod?: 'font' | 'manual_selection';
  userAgent?: string;
}): Promise<{ success: boolean; data?: { ticketNumber: string; companyName: string }; error?: string }> {
  const response = await fetch(`${API_BASE}/intake/sessions/${token}/submit`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  return await response.json() as { success: boolean; data?: { ticketNumber: string; companyName: string }; error?: string };
}

export async function requestRelink(token: string): Promise<{ success: boolean; error?: string }> {
  const response = await fetch(`${API_BASE}/intake/sessions/${token}/relink`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });
  return await response.json() as { success: boolean; error?: string };
}

export async function getDeviceInfo(deviceId: number): Promise<{
  success: boolean;
  data?: DeviceInfoResponse;
  error?: string;
}> {
  const response = await fetch(`${API_BASE}/intake/devices/${deviceId}`);
  return await response.json() as { success: boolean; data?: DeviceInfoResponse; error?: string };
}
